/**
 * @file ReceiptOutcomeFields.tsx
 * @description Поля исхода «не самовывоз»: перевозчик, адрес, стоимость,
 * комментарий (phase19.24 шаг 5.1, §2).
 *
 * Отделено от `ReceiptMethodFields`, чтобы обе части остались читаемыми:
 * карточки исходов — про выбор, эти поля — про условия. Какие поля показать,
 * решает реестр `RECEIPT_METHOD_META[method].requires`, а не каскад `if`:
 * новый исход добавляется записью реестра.
 *
 * Расчёт СДЭК здесь намеренно ручной: калькулятор (`DeliveryQuoteSheet`)
 * работает по уже существующему заказу — ему нужны позиции и места отгрузки.
 * При создании заказа менеджер ставит сумму руками, точный расчёт делает в
 * карточке (подсказка об этом стоит под полем).
 */

import type { ReactNode } from "react";

import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { NoticeStrip } from "../ui/NoticeStrip";
import { SelectField } from "../ui/SelectField";
import { Textarea } from "../ui/textarea";
import { ReceiptAddressField } from "./ReceiptAddressField";
import { SHIPMENT_CARRIER_LABEL, type ShipmentCarrier } from "../../lib/api-orders";
import { RECEIPT_METHOD_META, type ReceiptFormValue, type ReceiptIssue } from "../../lib/receipt-method";

interface Props {
  value: ReceiptFormValue;
  onChange: (next: ReceiptFormValue) => void;
  issue: ReceiptIssue | null;
  disabled?: boolean;
  idPrefix: string;
  /** Блок расчёта перевозчика между адресом и стоимостью (шаг 5.2). */
  quoteSlot?: ReactNode;
  /** Подпись под полем стоимости вместо подсказки по умолчанию. */
  amountHint?: string;
}

/**
 * Перевозчики ручного режима: весь реестр, кроме самовывоза — у него везти
 * нечего и позиции-услуги «Доставка» не бывает.
 */
const CARRIER_OPTIONS = (
  Object.entries(SHIPMENT_CARRIER_LABEL) as [ShipmentCarrier, string][]
)
  .filter(([code]) => code !== "PICKUP")
  .map(([value, label]) => ({ value, label }));

export function ReceiptOutcomeFields({
  value,
  onChange,
  issue,
  disabled,
  idPrefix,
  quoteSlot,
  amountHint,
}: Props) {
  const meta = RECEIPT_METHOD_META[value.method];
  const patch = (partial: Partial<ReceiptFormValue>): void =>
    onChange({ ...value, ...partial });
  const fieldError = (field: ReceiptIssue["field"]): string | null =>
    issue?.field === field ? issue.message : null;

  return (
    <div className="flex flex-col gap-4">
      {meta.requires.carrier ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-carrier`}>Перевозчик</Label>
          <SelectField
            id={`${idPrefix}-carrier`}
            ariaLabel="Перевозчик"
            value={value.carrier}
            onChange={(next) => patch({ carrier: next as ShipmentCarrier })}
            options={CARRIER_OPTIONS}
            placeholder="Выберите перевозчика"
            disabled={disabled}
          />
          {fieldError("carrier") ? (
            <p className="text-sm text-destructive">{fieldError("carrier")}</p>
          ) : null}
        </div>
      ) : null}

      {meta.requires.address ? (
        <ReceiptAddressField
          id={`${idPrefix}-address`}
          label={value.method === "CARRIER" ? "Адрес доставки или ПВЗ" : "Адрес доставки"}
          value={value.address}
          onChange={(address, city) => patch({ address, city })}
          disabled={disabled}
          error={fieldError("address")}
        />
      ) : null}

      {/* Расчёт стоит между «куда» и «сколько»: сумма в поле ниже берётся
          именно из выбранного тарифа (шаг 5.2 §3). */}
      {quoteSlot}

      {meta.requires.amount ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-amount`}>Стоимость доставки, ₽</Label>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              id={`${idPrefix}-amount`}
              className="min-h-12 w-40"
              inputMode="decimal"
              placeholder="450.00"
              value={value.freeDelivery ? "0" : value.deliveryAmount}
              disabled={disabled || value.freeDelivery}
              aria-invalid={fieldError("amount") ? true : undefined}
              onChange={(e) => patch({ deliveryAmount: e.target.value })}
            />
            {/* 0 ₽ разрешён только своими силами (решение 2026-09-21 §7 п.1). */}
            {meta.minAmount === 0 ? (
              <label className="flex min-h-12 cursor-pointer items-center gap-2">
                <Checkbox
                  checked={value.freeDelivery}
                  disabled={disabled}
                  onCheckedChange={(checked) => patch({ freeDelivery: checked === true })}
                />
                <span className="text-sm text-on-surface">Бесплатно</span>
              </label>
            ) : null}
          </div>
          {fieldError("amount") ? (
            <p className="text-sm text-destructive">{fieldError("amount")}</p>
          ) : (
            // Про калькулятор здесь не упоминаем вовсе (решение §8): блок расчёта
            // видит только перевозчик с подключённой интеграцией, и он же
            // присылает свою подсказку через `amountHint`.
            <p className="text-xs text-on-surface-variant">
              {amountHint ?? "Сумма войдёт в заказ отдельной позицией-услугой «Доставка»."}
            </p>
          )}
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-comment`}>Комментарий для клиента</Label>
        <Textarea
          id={`${idPrefix}-comment`}
          rows={2}
          value={value.comment}
          disabled={disabled}
          placeholder="Например: отправим на следующий день после оплаты"
          onChange={(e) => patch({ comment: e.target.value })}
        />
      </div>

      {value.method === "CARRIER" ? (
        <NoticeStrip tone="warning">
          Доставка ТК — только предоплата: заказ уйдёт в сборку после оплаты (F31).
        </NoticeStrip>
      ) : null}
      {value.method === "SHOP_COURIER" ? (
        <NoticeStrip>
          Позиция «Доставка курьером магазина» попадёт в заказ; бесплатная — строкой 0 ₽.
        </NoticeStrip>
      ) : null}
    </div>
  );
}
